import { useState, useEffect, useRef } from 'react';
import { FaColumns } from 'react-icons/fa';

/**
 * useColumnVisibility — manages which optional columns are visible.
 * Persists to localStorage by storageKey.
 * @param {Array<{key, label}>} columns
 * @param {string}              storageKey
 */
export function useColumnVisibility(columns, storageKey) {
  const [visible, setVisible] = useState(() => {
    try {
      const stored = storageKey && localStorage.getItem(storageKey);
      if (stored) return JSON.parse(stored);
    } catch {}
    return Object.fromEntries(columns.map(c => [c.key, true]));
  });

  useEffect(() => {
    if (storageKey) localStorage.setItem(storageKey, JSON.stringify(visible));
  }, [visible, storageKey]);

  const toggle = (key) => setVisible(prev => ({ ...prev, [key]: !prev[key] }));
  const show   = (key) => visible[key] !== false;

  return { visible, toggle, show };
}

/**
 * ColumnPicker — "Columns" button + dropdown to toggle optional columns.
 * Renders only on screens smaller than xl; on xl+ all columns always show.
 */
export function ColumnPicker({ columns, visible, onToggle }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const hasHidden = Object.values(visible).some(v => v === false);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="flex items-center justify-end px-4 py-2 border-b border-gray-100 xl:hidden">
      <div className="relative" ref={ref}>
        <button
          onClick={() => setOpen(p => !p)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 hover:text-[#0c1b4d] hover:bg-gray-100 rounded-lg transition-colors"
        >
          <FaColumns size={11} /> Columns
          {hasHidden && (
            <span className="ml-0.5 w-1.5 h-1.5 bg-[#0c1b4d] rounded-full inline-block" />
          )}
        </button>
        {open && (
          <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-gray-200 rounded-xl shadow-lg z-30 py-2">
            {columns.map(c => (
              <label key={c.key} className="flex items-center gap-2.5 px-4 py-1.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={visible[c.key] !== false}
                  onChange={() => onToggle(c.key)}
                  className="w-3.5 h-3.5 accent-[#0c1b4d]"
                />
                {c.label}
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
